import { useEffect, useRef, useState } from "react";
import { ticketService } from "../services/api/ticketService";

/** Window event other components dispatch after they mark something
 *  as read (e.g. opening a ticket detail). The sidebar badge listens
 *  and re-fetches so it doesn't have to wait up to a full poll cycle
 *  to update. */
export const TICKETS_UNREAD_EVENT = "tickets-unread:refresh";

/** Fire-and-forget: tell any mounted `useTicketsUnreadCount` to
 *  refetch immediately. Safe to call from anywhere. */
export const notifyTicketsUnreadChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(TICKETS_UNREAD_EVENT));
  }
};

/**
 * Polls /api/v1/tickets/unread-count/ every `intervalMs` (default 60s)
 * AND re-fetches immediately when any component dispatches the
 * `tickets-unread:refresh` window event. Returns the current ticket-
 * level unread count and a manual `refresh` callback.
 */
export function useTicketsUnreadCount(intervalMs = 60000) {
  const [tickets, setTickets] = useState(0);
  const [messages, setMessages] = useState(0);
  const timerRef = useRef<number | null>(null);

  const fetchCount = async () => {
    try {
      const c = await ticketService.unreadCount();
      setTickets(c.tickets);
      setMessages(c.messages);
    } catch {
      // Silent — keeps the previous value rather than flickering badges.
    }
  };

  useEffect(() => {
    void fetchCount();
    timerRef.current = window.setInterval(fetchCount, intervalMs);
    const onRefresh = () => {
      void fetchCount();
    };
    window.addEventListener(TICKETS_UNREAD_EVENT, onRefresh);
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
      window.removeEventListener(TICKETS_UNREAD_EVENT, onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return { tickets, messages, refresh: fetchCount };
}
